<div class="data-explorer-container">
  <!-- Chart Area -->
  <div class="chart-area">
    <div class="chart-header">
      <h2 class="chart-title">{{ 'monitoring.data-explorer.title' | translate }}</h2>
    </div>
    
    <div class="chart-wrapper">
      @if (loading()) {
        <div class="loading-overlay">
          <p-progress-spinner strokeWidth="4" />
        </div>
      }

      @if (!hasData() && !loading()) {
        <div class="empty-state">
          <i class="fa-solid fa-chart-line"></i>
          <p>{{'monitoring.data-explorer.empty-chart' | translate}}</p>
        </div>
      }

      <canvas #chartCanvas [hidden]="!hasData() || loading()"></canvas>
    </div>

    @if (errorMessage()) {
      <p-message severity="error" >
        {{ errorMessage() | translate }}
      </p-message>
    }
  </div>
  
  <!-- Controls Panel -->
  <div class="controls-panel">
    <h3 class="controls-title">{{'monitoring.data-explorer.controls.title' | translate}}</h3>
    
    <form [formGroup]="explorerForm">
      <!-- SECTION 1: DATA SELECTION -->
      <div class="section-label mb-2">{{'monitoring.data-explorer.controls.section-data.title' | translate}}</div>
      
      <!-- Measurement Type -->
      <div class="control-group mb-2">
        <p-iftalabel>
          <p-select 
            formControlName="measurementType"
            id="measurementType"
            [options]="availableMeasurementTypes"
            appendTo="body"
            placeholder="{{'monitoring.labels.measurementType' | translate}}"
            [showClear]="true"
            fluid/>
          <label for="measurementType">{{'monitoring.labels.measurementType' | translate}}</label>
        </p-iftalabel>
      </div>

      <!-- Variable to Display -->
      <div class="control-group mb-2">
        <p-iftalabel>
          <p-select 
            formControlName="metricField"
            id="metricField"
            [options]="filteredMetricFields"
            optionLabel="label"
            appendTo="body"
            placeholder="{{'monitoring.data-explorer.controls.section-data.metric-field-placeholder' | translate}}"
            fluid/>
          <label for="metricField">{{'monitoring.data-explorer.controls.section-data.metric-field' | translate}}</label>
        </p-iftalabel>
      </div>
      
      <!-- Filtering Section -->
      <div class="filter-section mb-2">
        <div class="flex justify-between items-center mb-2">
          <span class="filter-label">{{'monitoring.data-explorer.controls.section-data.filter.title' | translate}}</span>
          @if (canAddFilter()) {
            <button pButton type="button" 
                    icon="fa-solid fa-plus" 
                    size="small"
                    (click)="addFilter()">
            </button>
          }
        </div>
        
        @if (filtersArray.length === 0) {
          <span class="no-filters-hint">{{'monitoring.data-explorer.controls.section-data.filter.no-filters' | translate}}</span>
        }
        
        @if (filtersArray.length > 1) {
          <div class="flex justify-center mb-2">
            <p-selectbutton 
              [options]="filterLogicOptions" 
              formControlName="filterLogic"
              optionLabel="label" 
              optionValue="key"
              size="small"/>
          </div>
        }
        
        <div formArrayName="filters">
          @for (filter of filtersArray.controls; track $index) {
            <div class="filter-row mb-2" [formGroupName]="$index">
              <div class="flex flex-column align-center flex-wrap gap-2">
                <div class="flex flex-row w-full">
                    <p-select 
                    formControlName="field"
                    [options]="getAvailableFilterFields($index)"
                    optionLabel="label"
                    appendTo="body"
                    placeholder="{{'monitoring.data-explorer.controls.section-data.filter.field-placeholder' | translate}}"
                    (onChange)="onFilterFieldChange($index)"
                    size="small"
                    fluid/>
                    <button pButton type="button" 
                            icon="fa-solid fa-times" 
                            [text]="true"
                            severity="danger"
                            size="small"
                            (click)="removeFilter($index)">
                    </button>
                </div>                
                @if (getFilterField($index); as field) {
                  @if (field.filterType === 'categorical') {
                    @if (filterStates[$index]?.loading) {
                      <p-progress-spinner strokeWidth="4" [style]="{width: '20px', height: '20px'}"/>
                    } @else {
                      <p-multiselect 
                        formControlName="selectedValues"
                        [options]="filterStates[$index]?.availableValues || []"
                        optionLabel="value"
                        optionValue="value"
                        appendTo="body"
                        placeholder="{{'monitoring.data-explorer.controls.section-data.filter.value-placeholder' | translate}}"
                        [showClear]="true"
                        size="small"
                        fluid/>
                    }
                  }
                  
                  @if (field.filterType === 'numeric') {
                    <p-select 
                      formControlName="operator"
                      [options]="numericOperators"
                      optionLabel="label"
                      optionValue="key"
                      appendTo="body"
                      size="small"
                      class="filter-operator"
                      fluid/>
                    <p-inputnumber 
                      formControlName="numericValue"
                      [showButtons]="false"
                      size="small"
                      class="filter-numeric-input"
                      fluid/>
                  }
                }       
              </div>
            </div>
          }
        </div>
      </div>
      
      <!-- Grouping -->
      <div class="control-group mb-2">
        <p-iftalabel>
          <p-select 
            formControlName="groupByField"
            id="groupByField"
            [options]="filteredGroupByFields"
            optionLabel="label"
            appendTo="body"
            [showClear]="true"
            placeholder="{{'monitoring.data-explorer.controls.section-grouping.group-by-placeholder' | translate}}" 
            fluid/>
          <label for="groupByField">{{'monitoring.data-explorer.controls.section-grouping.group-by' | translate}}</label>
        </p-iftalabel>
      </div>

      @if (explorerForm.get('groupByField')?.value) {
        <div class="control-group mb-2">
          <p-iftalabel>
            <p-select 
              formControlName="groupByField2"
              id="groupByField2"
              [options]="getAvailableGroupBy2Fields()"
              optionLabel="label"
              appendTo="body"
              [showClear]="true"
              placeholder="{{'monitoring.data-explorer.controls.section-grouping.group-by-placeholder' | translate}}" 
              fluid/>
            <label for="groupByField2">{{'monitoring.data-explorer.controls.section-grouping.group-by' | translate}} (2)</label>
          </p-iftalabel>
        </div>
      }

      <p-divider></p-divider>
      
      <!-- SECTION 2: DATE RANGE -->
      <div class="section-label mb-2">{{'monitoring.data-explorer.controls.section-data.date-range' | translate}}</div>
      <div class="control-group mb-2">
        <p-iftalabel>
          <p-datepicker 
            formControlName="dateRange"
            selectionMode="range"
            id="dateRange"
            [showIcon]="true"
            [showButtonBar]="true"
            appendTo="body"
            dateFormat="dd/mm/yy"
            placeholder="{{'monitoring.data-explorer.controls.section-data.date-range-placeholder' | translate}}"
            fluid/>
          <label for="dateRange">{{'monitoring.data-explorer.controls.section-data.date-range' | translate}}</label>
        </p-iftalabel>
      </div>

      <p-divider></p-divider>
            
      <!-- SECTION 3: AGGREGATION -->
      <div class="section-label mb-2">{{'monitoring.data-explorer.controls.section-aggregation.title' | translate}}</div>
      <div class="control-group mb-2">
        <p-iftalabel>
          <p-select 
            formControlName="timeAggregation"
            id="timeAggregation"
            [options]="timeAggregations"
            optionLabel="label"
            appendTo="body"
            placeholder="{{'monitoring.data-explorer.controls.section-aggregation.time-aggregation-placeholder' | translate}}"
            fluid/>
          <label for="timeAggregation">{{'monitoring.data-explorer.controls.section-aggregation.time-aggregation' | translate}}</label>
        </p-iftalabel>
      </div>
      
      @if (showAggregationFunction()) {
        <div class="control-group mb-2">
          <p-iftalabel>
            <p-select 
              formControlName="aggregation"
              id="aggregation"
              [options]="aggregationFunctions"
              optionLabel="label"
              appendTo="body"
              placeholder="{{'monitoring.data-explorer.controls.section-aggregation.aggregation-function-placeholder' | translate}}"
              fluid/>
            <label for="aggregation">{{'monitoring.data-explorer.controls.section-aggregation.aggregation-function' | translate}}</label>
          </p-iftalabel>
        </div>
      }
      
      <p-divider></p-divider>
      <div class="apply-button">
        <p-button 
          label="{{'monitoring.data-explorer.controls.apply' | translate}}"
          icon="fa-solid fa-play"
          [loading]="loading()"
          [disabled]="explorerForm.invalid"
          class="w-full"
          (onClick)="onApply()" />
      </div>
    </form>
  </div>
</div>
